import React, { useMemo, useRef } from 'react';
import {
	projectTypeToCanonicalId,
	toProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/analytics-bridge';
import { useMemoizedTrialDaysLeftFromTrialEndTime } from '@atlassian/upgrades-and-purchases/get-trial-days-left';
import { fg } from '@atlassian/jira-feature-gating';
import { isStartOfTrial, isProjectTypeAndEditionSupported } from './src/ui/common/utils/index.tsx';
import { endOfTrialExtensionCoordinatorEntryPoint } from './src/ui/entrypoint.tsx';

/**
 * This is the top level component of the experiment, responsible for checking the entitlement and project context
 * to decide if EP and Statsig should be checked or not
 */
export const EndOfTrialExtension = () => {
	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);

	const appEditions = useAppEditions();

	const productEntitlementDetails = useProductEntitlementDetails();
	const productKey = projectType && projectTypeToCanonicalId(projectType);
	const entitlementDetails = productKey && productEntitlementDetails?.[productKey];

	const billingSourceSystem = entitlementDetails?.billingSourceSystem;

	const trialDaysLeft =
		useMemoizedTrialDaysLeftFromTrialEndTime(entitlementDetails?.trialEndTime) ?? 0;

	const shouldLoadEntryPoint =
		isProjectTypeAndEditionSupported(appEditions, project?.type) &&
		billingSourceSystem === 'CCP' &&
		isStartOfTrial(trialDaysLeft);

	const { entryPointReferenceSubject } = useCoordinatorEntryPoint(shouldLoadEntryPoint);

	if (!shouldLoadEntryPoint) return null;

	return (
		<ContextualAnalyticsData attributes={{ trialDaysLeft, growthInitiative: 'buy' }}>
			<JiraEntryPointContainer
				id="growth.endOfTrialExtension.coordinator"
				packageName="jira-end-of-trial-extension"
				entryPointReferenceSubject={entryPointReferenceSubject}
				fallback={null}
				runtimeProps={{
					trialDaysLeft,
				}}
			/>
		</ContextualAnalyticsData>
	);
};

export default EndOfTrialExtension;

const useCoordinatorEntryPoint = (shouldLoad: boolean) => {
	const cloudID = useCloudId();
	const entryPointParams = useMemo(
		() => ({ cloudId: cloudID, isTwcJiraTouchPointFix: fg('twc_jira_touchpoint_fix') }),
		[cloudID],
	);
	const hasLoadedEntrypoint = useRef(false);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		endOfTrialExtensionCoordinatorEntryPoint,
		entryPointParams,
	);

	if (!hasLoadedEntrypoint.current && shouldLoad) {
		entryPointActions.load();
		hasLoadedEntrypoint.current = true;
	}

	return { entryPointReferenceSubject };
};
